import axios, { AxiosRequestConfig } from 'axios';

// Sin timeout, una petición que el servidor nunca responde (o una conexión que se cae en
// silencio) deja la promesa colgada para siempre: la UI se queda esperando sin error ni
// feedback. 120s es holgado para las operaciones más pesadas del sistema —guardado de
// borrador de cuestionario, carga masiva de terceros, subida de documentos— y aun así
// garantiza que toda petición termine en algún momento.
const REQUEST_TIMEOUT_MS = 120_000;

const axiosServices = axios.create({
  baseURL: import.meta.env.VITE_APP_API_URL || 'http://localhost:3010/',
  timeout: REQUEST_TIMEOUT_MS
});

const tokenStorageKey = 'serviceToken';

function getAccessToken(): string | null {
  return localStorage.getItem(tokenStorageKey) ?? sessionStorage.getItem(tokenStorageKey);
}

function clearAccessToken(): void {
  localStorage.removeItem(tokenStorageKey);
  sessionStorage.removeItem(tokenStorageKey);
}

// ==============================|| AXIOS - FOR MOCK SERVICES ||============================== //

axiosServices.interceptors.request.use(
  async (config) => {
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosServices.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      if (!isLoginRequest) {
        clearAccessToken();
        redirectWithBasePath('/login');
      }
    }
    return Promise.reject((error.response && error.response.data) || 'Wrong Services');
  }
);

export default axiosServices;

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosServices.get(url, { ...config });

  return res.data;
};

export const fetcherPost = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosServices.post(url, { ...config });

  return res.data;
};

/**
 * sessionStorage no se comparte entre pestañas. Cuando el admin abre una nueva pestaña
 * (p.ej. "Llenar cuestionario" en modo interno), el JWT debe estar en localStorage para
 * que el interceptor lo incluya en las peticiones. Esta función lo copia si hace falta.
 */
export function ensureTokenAvailableCrossTab(): void {
  const key = 'serviceToken';
  if (!localStorage.getItem(key)) {
    const sessionToken = sessionStorage.getItem(key);
    if (sessionToken) localStorage.setItem(key, sessionToken);
  }
}

export function redirectWithBasePath(path: string) {
  const basePath = import.meta.env.VITE_APP_BASE_NAME || process.env.VITE_APP_BASE_NAME || '';
  window.location.pathname = `${basePath.replace(/\/$/, '')}${path}`;
}
